import { useMemo, useState, type FormEvent } from "react";
import { useLocale } from "../../shared/i18n/LocaleContext";
import { useAuth } from "../auth/AuthContext";
import { saveFinance } from "../finance/financeApi";
import type { TranslationKey } from "../../shared/i18n/translations";
import {
  changeCommercialStatus,
  convertPurchasingQuotation,
  convertSalesQuotation,
  purchasingDocumentUrl,
  saveCommercial,
  salesDocumentUrl,
  sendSalesDocument,
  sendPurchasingDocument,
  type CommercialRecord,
  type CommercialSnapshot,
} from "./commercialApi";
import type { CommercialModule, CommercialSection } from "./CommercialPage";
import { AppModal } from "../../shared/components/AppModal";

type Line = {
  articleId: string;
  unitId: string;
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  taxPercent: number;
  sourceLineId: string;
  lotNumber: string;
  serialNumber: string;
};
const k = (v: string) => v as TranslationKey;
const entity: Record<string, string> = {
  customers: "customers",
  suppliers: "suppliers",
  supplierArticles: "supplier-articles",
  requisitions: "requisitions",
  quotations: "quotations",
  orders: "orders",
  deliveries: "deliveries",
  receipts: "receipts",
  invoices: "invoices",
  returns: "returns",
  configuration: "counters",
};
const blank = (): Line => ({
  articleId: "",
  unitId: "",
  quantity: 1,
  unitPrice: 0,
  discountPercent: 0,
  taxPercent: 21,
  sourceLineId: "",
  lotNumber: "",
  serialNumber: "",
});

export function CommercialRecordPage({
  module,
  section,
  item,
  data,
  onCancel,
  onSaved,
  onPrevious,
  onNext,
}: {
  module: CommercialModule;
  section: CommercialSection;
  item: CommercialRecord | null;
  data: CommercialSnapshot;
  onCancel: () => void;
  onSaved: () => Promise<void>;
  onPrevious?: (() => void) | null;
  onNext?: (() => void) | null;
}) {
  const { t } = useLocale();
  const { user } = useAuth();
  const master = ["customers", "suppliers"].includes(section),
    supplierArticle = section === "supplierArticles",
    counter = section === "configuration",
    document = !master && !supplierArticle && !counter;
  const sourceCollection =
    section === "quotations"
      ? "quotationLines"
      : section === "requisitions"
        ? "requisitionLines"
          : section === "invoices"
          ? "invoiceLines"
          : section === "returns"
            ? "returnLines"
          : section === "deliveries"
            ? "deliveryLines"
            : section === "receipts"
              ? "receiptLines"
              : "orderLines";
  const existingLines = (data[sourceCollection] ?? []) as CommercialRecord[];
  const relatedLines = useMemo(
    () =>
      existingLines.filter(
        (line) =>
          !item ||
          line[parentIdField(module, section)] === item.id,
      ),
    [existingLines, item, module, section],
  );
  const [lines, setLines] = useState<Line[]>(
    relatedLines.length
      ? relatedLines.map((line) => ({
          articleId: String(line.articleId ?? ""),
          unitId: String(line.unitId ?? ""),
          quantity: Number(line.quantity ?? 1),
          unitPrice: Number(line.unitPrice ?? 0),
          discountPercent: Number(line.discountPercent ?? 0),
          taxPercent: Number(line.taxPercent ?? 21),
          sourceLineId: String(line.id ?? ""),
          lotNumber: "",
          serialNumber: "",
        }))
      : [blank()],
  );
  const [error, setError] = useState<string>();
  const [documentRecipient,setDocumentRecipient]=useState("");
  const [showDocumentSend,setShowDocumentSend]=useState(false);
  const orders = (data.orders ?? []) as CommercialRecord[];
  const sourceOrderLines = (data.orderLines ?? []) as CommercialRecord[];
  const sourceReceiptLines = (data.receiptLines ?? []) as CommercialRecord[];
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body: Record<string, unknown> = Object.fromEntries(
      new FormData(e.currentTarget),
    );
    for (const name of [
      "creditLimit",
      "leadTimeDays",
      "unitPrice",
      "minimumQuantity",
      "nextNumber",
      "padding",
    ])
      if (body[name]) body[name] = Number(body[name]);
    for (const name of ["isActive", "isPreferred", "isCreditNote"])
      body[name] = Boolean(
        (e.currentTarget.elements.namedItem(name) as HTMLInputElement | null)?.checked,
      );
    if (body.correctedInvoiceId === "") body.correctedInvoiceId = null;
    if (document)
      body.lines = lines.map((line) => ({
        ...line,
        unitId: line.unitId || null,
        sourceLineId: line.sourceLineId || null,
        requiredAtUtc: body.expectedAtUtc ?? body.requestedAtUtc,
      }));
    try {
      await saveCommercial(module, entity[section], item?.id, body);
      await onSaved();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "commercial.saveError",
      );
    }
  };
  const transition =
    section === "invoices" &&
    item &&
    user?.permissions.finance === "Write" &&
    !((data.accountedInvoiceIds ?? []) as string[]).includes(item.id)
      ? "Accounting"
      : section === "quotations" &&
          item?.status === "Accepted"
        ? "Converted"
        : nextStatus(item?.status, section, module);
  const applyStatus = async () => {
    if (!item || !transition) return;
    try {
      if (transition === "Converted") {
        if (module === "sales") await convertSalesQuotation(item.id);
        else await convertPurchasingQuotation(item.id);
      }
      else if (transition === "Accounting")
        await saveFinance(
          module === "sales" ? "sales-invoice" : "purchase-invoice",
          { sourceId: item.id },
        );
      else
        await changeCommercialStatus(
          module,
          entity[section],
          item.id,
          transition,
        );
      await onSaved();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "commercial.saveError",
      );
    }
  };
  const update = (index: number, name: keyof Line, value: string | number) =>
    setLines((all) =>
      all.map((line, i) => (i === index ? { ...line, [name]: value } : line)),
    );
  const party = (data[module === "sales" ? "customers" : "suppliers"] ??
    []) as CommercialRecord[];
  const isSalesDocument=module==="sales"&&Boolean(item)&&["quotations","orders","deliveries","invoices"].includes(section);
  const isPurchasingDocument=module==="purchasing"&&Boolean(item)&&["quotations","orders","receipts","invoices"].includes(section);
  const sendPdf=async()=>{if(!item)return;try{if(module==="sales")await sendSalesDocument(entity[section],item.id,documentRecipient);else await sendPurchasingDocument(entity[section],item.id,documentRecipient);setShowDocumentSend(false);setDocumentRecipient("");await onSaved()}catch(reason){setError(reason instanceof Error?reason.message:`${module}.documentSendFailed`)}};
  return (
    <main className="page-content">
      <AppModal
        title={t(k(`${module}.${section}`))}
        closeLabel={t("recordNavigation.backToList")}
        onClose={onCancel}
        onPrevious={onPrevious}
        onNext={onNext}
        saveForm
        wide
        feedback={error ? t(k(error)) : null}
        headerActions={<>
          {isSalesDocument&&<a className="secondary-button" href={salesDocumentUrl(entity[section],item!.id)} target="_blank" rel="noreferrer">{t(k("sales.downloadPdf"))}</a>}
          {isPurchasingDocument&&<a className="secondary-button" href={purchasingDocumentUrl(entity[section],item!.id)} target="_blank" rel="noreferrer">{t(k("sales.downloadPdf"))}</a>}
          {isSalesDocument&&<button className="secondary-button" type="button" onClick={()=>setShowDocumentSend(value=>!value)}>{t(k("sales.sendPdf"))}</button>}
          {isPurchasingDocument&&<button className="secondary-button" type="button" onClick={()=>setShowDocumentSend(value=>!value)}>{t(k("sales.sendPdf"))}</button>}
          {transition && (
            <button
              className="secondary-button"
              type="button"
              onClick={() => void applyStatus()}
            >
              {t(k(`commercial.status.${transition}`))}
            </button>
          )}
        </>}
      >
      {showDocumentSend&&<section className="data-panel administration-panel" aria-label={t(k("sales.sendPdf"))}><div className="administration-form"><label><span>{t(k("sales.recipient"))}</span><input type="email" value={documentRecipient} onChange={event=>setDocumentRecipient(event.target.value)} required/></label><button className="primary-button" type="button" disabled={!documentRecipient} onClick={()=>void sendPdf()}>{t(k("sales.sendPdf"))}</button></div></section>}
      <section className="data-panel administration-panel">
        <form
          id="commercial-record-form"
          className="administration-form"
          onSubmit={submit}
        >
          {!(module === "sales" && section === "invoices") && (
            <label>
              <span>
                {t(
                  master || supplierArticle
                    ? "aplPanel.code"
                    : "quality.number",
                )}
              </span>
              <input
                name={
                  master ? "code" : supplierArticle ? "supplierCode" : "number"
                }
                defaultValue={String(item?.code ?? item?.number ?? "")}
                required
              />
            </label>
          )}
          {master && (
            <>
              <label>
                <span>{t("aplPanel.name")}</span>
                <input
                  name="name"
                  defaultValue={String(item?.name ?? "")}
                  required
                />
              </label>
              <label>
                <span>{t(k("commercial.currency"))}</span>
                <input
                  name="currencyCode"
                  defaultValue={String(item?.currencyCode ?? "EUR")}
                  maxLength={3}
                  required
                />
              </label>
              <label><span>{t("sales.taxIdentifier")}</span><input name="taxIdentifier" defaultValue={String(item?.taxIdentifier ?? "")} /></label>
              <label><span>{t("sales.email")}</span><input name="email" type="email" defaultValue={String(item?.email ?? "")} /></label>
              <label><span>{t("sales.phone")}</span><input name="phone" defaultValue={String(item?.phone ?? "")} /></label>
              {module === "purchasing" && <>
                <label><span>{t("purchasing.paymentTerms")}</span><select name="paymentTerms" defaultValue={String(item?.paymentTerms ?? "")}><option value="">—</option>{((data.paymentTerms ?? []) as CommercialRecord[]).filter(x => x.isActive !== false).map(x => <option key={x.id} value={String(x.code)}>{String(x.code)} · {String(x.name)}</option>)}</select></label>
                <label><span>{t("purchasing.leadTimeDays")}</span><input name="leadTimeDays" type="number" min="0" defaultValue={String(item?.leadTimeDays ?? 0)} /></label>
              </>}
              <label className="checkbox-label"><input name="isActive" type="checkbox" defaultChecked={item?.isActive !== false} /><span>{t("administration.active")}</span></label>
            </>
          )}
          {supplierArticle && (
            <>
              <RecordSelect
                label={t(k("purchasing.supplier"))}
                name="supplierId"
                records={data.suppliers as CommercialRecord[]}
              />
              <ArticleSelect
                data={data}
                name="articleId"
                label={t(k("commercial.article"))}
              />
              <label><span>{t("purchasing.supplierDescription")}</span><input name="supplierDescription" defaultValue={String(item?.supplierDescription ?? "")} /></label>
              <label><span>{t("commercial.unitPrice")}</span><input name="unitPrice" type="number" min="0" step="any" defaultValue={String(item?.unitPrice ?? 0)} required /></label>
              <label><span>{t("commercial.currency")}</span><input name="currencyCode" maxLength={3} defaultValue={String(item?.currencyCode ?? "EUR")} required /></label>
              <label><span>{t("purchasing.minimumQuantity")}</span><input name="minimumQuantity" type="number" min="0" step="any" defaultValue={String(item?.minimumQuantity ?? 0)} /></label>
              <label><span>{t("purchasing.leadTimeDays")}</span><input name="leadTimeDays" type="number" min="0" defaultValue={String(item?.leadTimeDays ?? 0)} /></label>
              <label className="checkbox-label"><input name="isPreferred" type="checkbox" defaultChecked={Boolean(item?.isPreferred)} /><span>{t("purchasing.preferred")}</span></label>
            </>
          )}
          {document && (
            <>
              {!['requisitions', 'deliveries', 'receipts', 'returns'].includes(section) && <RecordSelect
                label={t(module === "sales" ? k("sales.customer") : k("purchasing.supplier"))}
                name={module === "sales" ? "customerId" : "supplierId"}
                records={party}
              />}
              {!["deliveries", "receipts", "returns"].includes(section) && (
                <label>
                  <span>{t(k("commercial.documentDate"))}</span>
                  <input
                    name="documentDate"
                    type="date"
                    defaultValue={String(
                      item?.orderDate ??
                        item?.quotationDate ??
                        item?.issueDate ??
                        item?.requestDate ??
                        "",
                    )}
                    required
                  />
                </label>
              )}
              {section === "quotations" && (
                <label>
                  <span>{t(k("commercial.validUntil"))}</span>
                  <input
                    name="validUntil"
                    type="date"
                    defaultValue={String(item?.validUntil ?? "")}
                    required
                  />
                </label>
              )}
              {section === "orders" && (
                <label>
                  <span>{t(k("commercial.expectedAt"))}</span>
                  <input
                    name={
                      module === "sales" ? "requestedAtUtc" : "expectedAtUtc"
                    }
                    type="datetime-local"
                    required
                  />
                </label>
              )}
              {module === "sales" && section === "invoices" && (
                <>
                  <label>
                    <span>{t("commercial.series")}</span>
                    <select name="series" required>
                      {((data.counters ?? []) as CommercialRecord[]).map(
                        (x) => (
                          <option key={x.id} value={String(x.series)}>
                            {String(x.series)} · {String(x.prefix ?? "")}
                          </option>
                        ),
                      )}
                    </select>
                  </label>
                  <label>
                    <span>{t(k("sales.invoiceKind"))}</span>
                    <select name="invoiceKind" defaultValue="Full">
                      <option value="Full">{t(k("sales.invoiceFull"))}</option>
                      <option value="Simplified">
                        {t(k("sales.invoiceSimplified"))}
                      </option>
                      <option value="Rectifying">
                        {t(k("sales.invoiceRectifying"))}
                      </option>
                    </select>
                  </label>
                  <label>
                    <span>{t(k("sales.operationDate"))}</span>
                    <input name="operationDate" type="date" />
                  </label>
                  <label>
                    <span>{t(k("sales.dueDate"))}</span>
                    <input name="dueDate" type="date" />
                  </label>
                  <label>
                    <span>{t(k("sales.correctedInvoice"))}</span>
                    <select name="correctedInvoiceId">
                      <option value="">—</option>
                      {((data.invoices ?? []) as CommercialRecord[]).map(
                        (x) => (
                          <option key={x.id} value={x.id}>
                            {String(x.number)}
                          </option>
                        ),
                      )}
                    </select>
                  </label>
                </>
              )}
              {module === "purchasing" && section === "invoices" && <>
                <label><span>{t("purchasing.supplierInvoiceNumber")}</span><input name="externalDocument" required defaultValue={String(item?.supplierInvoiceNumber ?? "")} /></label>
                <label><span>{t("purchasing.dueDate")}</span><input name="dueDate" type="date" defaultValue={String(item?.dueDate ?? "")} /></label>
                <label className="checkbox-label"><input name="isCreditNote" type="checkbox" defaultChecked={Boolean(item?.isCreditNote)} /><span>{t("purchasing.creditNote")}</span></label>
                <label><span>{t("purchasing.correctedInvoice")}</span><select name="correctedInvoiceId"><option value="">—</option>{((data.invoices??[]) as CommercialRecord[]).filter(x=>!x.isCreditNote).map(x=><option key={x.id} value={x.id}>{String(x.number)} · {String(x.supplierInvoiceNumber??"")}</option>)}</select></label>
              </>}
              {["deliveries", "receipts"].includes(section) && (
                <>
                  <RecordSelect
                    label={t(k("commercial.order"))}
                    name={
                      module === "sales" ? "salesOrderId" : "purchaseOrderId"
                    }
                    records={orders}
                  />
                  <label>
                    <span>{t(k("commercial.warehouse"))}</span>
                    <select name="warehouseId" required>
                      {data.warehouses.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.code} · {x.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  {module === "purchasing" && <>
                    <label><span>{t("purchasing.externalDocument")}</span><input name="externalDocument" defaultValue={String(item?.supplierDocument ?? "")} /></label>
                    <label><span>{t("purchasing.receivedAt")}</span><input name="movementAtUtc" type="datetime-local" required /></label>
                  </>}
                </>
              )}
              {section === "returns" && <>
                <RecordSelect label={t("purchasing.receipts")} name="purchaseReceiptId" records={(data.receipts ?? []) as CommercialRecord[]} />
                <label><span>{t("purchasing.returnReason")}</span><input name="reason" required /></label>
                <label><span>{t("purchasing.returnedAt")}</span><input name="movementAtUtc" type="datetime-local" required /></label>
              </>}
            </>
          )}
          {counter && (
            <>
              <label>
                <span>{t(k("commercial.module"))}</span>
                <input
                  name="module"
                  defaultValue={String(item?.module ?? "")}
                  required
                />
              </label>
              <label>
                <span>{t(k("commercial.documentType"))}</span>
                <input
                  name="documentType"
                  defaultValue={String(item?.documentType ?? "")}
                  required
                />
              </label>
              <label>
                <span>{t(k("commercial.series"))}</span>
                <input
                  name="series"
                  defaultValue={String(item?.series ?? "")}
                  required
                />
              </label>
              <label>
                <span>{t(k("commercial.nextNumber"))}</span>
                <input
                  name="nextNumber"
                  type="number"
                  min="1"
                  defaultValue={String(item?.nextNumber ?? 1)}
                  required
                />
              </label>
            </>
          )}
          <label className="form-field-wide">
            <span>{t("administration.description")}</span>
            <textarea
              name={
                master
                  ? module === "sales"
                    ? "billingAddress"
                    : "address"
                  : "notes"
              }
              defaultValue={String(
                item?.notes ?? item?.address ?? item?.billingAddress ?? "",
              )}
            />
          </label>
        </form>
        {document && (
          <section aria-labelledby="commercial-lines-title">
            <div className="section-heading">
              <h2 id="commercial-lines-title">{t(k("commercial.lines"))}</h2>
              <button
                className="secondary-button"
                type="button"
                onClick={() => setLines((all) => [...all, blank()])}
              >
                + {t("administration.add")}
              </button>
            </div>
            {lines.map((line, index) => (
              <div className="administration-form" key={index}>
                {["deliveries", "receipts", "returns"].includes(section) ? (
                  <label>
                    <span>{t(k("commercial.sourceLine"))}</span>
                    <select
                      value={line.sourceLineId}
                      onChange={(e) => {
                        const availableLines=section==="returns"?sourceReceiptLines:sourceOrderLines;
                        const source = availableLines.find(
                          (x) => x.id === e.target.value,
                        );
                        update(index, "sourceLineId", e.target.value);
                        if (source) {
                          const sourceOrderLine=section==="returns"?sourceOrderLines.find(x=>x.id===source.purchaseOrderLineId):source;
                          update(index, "articleId", String(sourceOrderLine?.articleId ?? ""));
                        }
                      }}
                      required
                    >
                      <option value="">—</option>
                      {(section==="returns"?sourceReceiptLines:sourceOrderLines).map((x) => (
                        <option key={x.id} value={x.id}>
                          {String(x.lineNumber)} ·{" "}
                          {String(
                            data.articles.find((a) => a.id === (section==="returns"?sourceOrderLines.find(line=>line.id===x.purchaseOrderLineId)?.articleId:x.articleId))
                              ?.name ?? x.articleId,
                          )}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <ArticleSelect
                    data={data}
                    label={t(k("commercial.article"))}
                    value={line.articleId}
                    onChange={(value) => update(index, "articleId", value)}
                  />
                )}
                <label>
                  <span>{t("quality.quantity")}</span>
                  <input
                    type="number"
                    min="0.000001"
                    step="any"
                    value={line.quantity}
                    onChange={(e) =>
                      update(index, "quantity", Number(e.target.value))
                    }
                  />
                </label>
                {!["deliveries", "receipts", "returns", "requisitions"].includes(
                  section,
                ) && (
                  <>
                    <label>
                      <span>{t(k("commercial.unitPrice"))}</span>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={line.unitPrice}
                        onChange={(e) =>
                          update(index, "unitPrice", Number(e.target.value))
                        }
                      />
                    </label>
                    <label>
                      <span>{t(k("sales.taxRate"))}</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="any"
                        value={line.taxPercent}
                        onChange={(e) =>
                          update(index, "taxPercent", Number(e.target.value))
                        }
                      />
                    </label>
                  </>
                )}
                <button
                  className="danger-button"
                  type="button"
                  onClick={() =>
                    setLines((all) => all.filter((_, i) => i !== index))
                  }
                  aria-label={t(k("commercial.removeLine"))}
                >
                  ×
                </button>
              </div>
            ))}
          </section>
        )}
      </section>
      </AppModal>
    </main>
  );
}
function ArticleSelect({
  data,
  label,
  name,
  value,
  onChange,
}: {
  data: CommercialSnapshot;
  label: string;
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
}) {
  return (
    <label>
      <span>{label}</span>
      <select
        name={name}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        required
      >
        <option value="">—</option>
        {data.articles.map((x) => (
          <option key={x.id} value={x.id}>
            {x.code} · {x.name}
          </option>
        ))}
      </select>
    </label>
  );
}
function RecordSelect({
  label,
  name,
  records,
}: {
  label: string;
  name: string;
  records: CommercialRecord[];
}) {
  return (
    <label>
      <span>{label}</span>
      <select name={name} required>
        <option value="">—</option>
        {records.map((x) => (
          <option key={x.id} value={x.id}>
            {String(x.code ?? x.number)} · {String(x.name ?? "")}
          </option>
        ))}
      </select>
    </label>
  );
}
function parentIdField(module: CommercialModule, section: CommercialSection) {
  if (section === "quotations")
    return module === "sales" ? "salesQuotationId" : "supplierQuotationId";
  if (section === "requisitions") return "purchaseRequisitionId";
  if (section === "invoices")
    return module === "sales" ? "salesInvoiceId" : "purchaseInvoiceId";
  if (section === "deliveries") return "salesDeliveryId";
  if (section === "receipts") return "purchaseReceiptId";
  if (section === "returns") return "purchaseReturnId";
  return module === "sales" ? "salesOrderId" : "purchaseOrderId";
}
function nextStatus(
  status: unknown,
  section: CommercialSection,
  module: CommercialModule,
) {
  if (section === "quotations" && module === "sales" && status === "Sent")
    return "Accepted";
  if (section === "quotations" && module === "purchasing") {
    if (status === "Requested") return "Received";
    if (status === "Received") return "Accepted";
  }
  if (section === "requisitions" && status === "Submitted") return "Approved";
  if (status !== "Draft") return undefined;
  if (section === "orders") return "Confirmed";
  if (section === "quotations")
    return module === "sales" ? "Sent" : "Requested";
  if (section === "requisitions") return "Submitted";
  return undefined;
}
